/**
 * @component MotionContext
 * @description The application's motion preference, and the body classes the
 * stylesheets read it from
 * @stateType global (Context-based state shared across the application)
 * @accessibility WCAG AA compliant with reduced motion preference support
 * @performance Context value changes only when the preference itself changes
 *
 * @businessContext
 * Decorative keyframes are the one part of the interface a user can turn off,
 * and the switch has to reach both React components and plain stylesheets, so
 * the preference is held here and mirrored onto document.body.
 *
 * @dataFlow
 * System Accessibility -> Motion Preferences -> Context State -> Body Classes
 *
 * @dependencies
 * - React Context API for global state management
 * - Browser MediaQuery API for motion preference detection
 *
 * The context object and the useMotionPreference hook live in
 * ./motionPreference so this file exports the provider alone and stays a
 * React Fast Refresh boundary.
 */

import React, { useEffect, useState, memo } from 'react'
import { MotionContext } from './motionPreference'

/**
 * Props interface for MotionProvider component
 *
 * @interface MotionProviderProps
 * @property {React.ReactNode} children - Child components to receive the motion context
 * @businessContext Provides the motion context to all child components in the app
 */
interface MotionProviderProps {
  /**
   * @property {React.ReactNode} children - React component tree to provide context to
   * @businessContext All components that need access to the motion preference
   * @dataSource Application component tree requiring the motion preference
   */
  children: React.ReactNode
}

/**
 * Provider for the application-wide motion preference
 *
 * @component MotionProvider
 * @param {MotionProviderProps} props - Component properties
 * @returns {JSX.Element} Context provider with the motion preference state
 * @businessLogic Manages the global motion preference and mirrors it onto the body
 * @accessibility Automatically detects and respects reduced motion preferences
 */
export const MotionProvider: React.FC<MotionProviderProps> = memo(({ children }) => {
  const [animationsEnabled, setAnimationsEnabled] = useState(true)

  /**
   * Initialize accessibility preferences and motion detection
   *
   * @effect initializeAccessibilityPreferences
   * @businessLogic Detects system reduced motion preference and sets initial animation state
   * @sideEffects Updates animationsEnabled state based on system preferences
   */
  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setAnimationsEnabled(!mediaQuery.matches)

    /**
     * Handle media query changes for motion preferences
     *
     * @callback handleMotionPreferenceChange
     * @param {MediaQueryListEvent} e - Media query change event
     * @businessLogic Updates animation state when user changes motion preferences
     * @sideEffects Updates animationsEnabled state
     */
    const handleMotionPreferenceChange = (e: MediaQueryListEvent) => {
      setAnimationsEnabled(!e.matches)
    }

    mediaQuery.addEventListener('change', handleMotionPreferenceChange)
    return () => mediaQuery.removeEventListener('change', handleMotionPreferenceChange)
  }, [])

  /**
   * Apply animation state to document body
   *
   * @effect applyAnimationClasses
   * @businessLogic Updates document body classes to control global animation behavior
   * @sideEffects Modifies document.body.classList for CSS animation control
   */
  useEffect(() => {
    // Apply animation class to body
    if (animationsEnabled) {
      document.body.classList.add('animations-enabled')
      document.body.classList.remove('animations-disabled')
    } else {
      document.body.classList.add('animations-disabled')
      document.body.classList.remove('animations-enabled')
    }
  }, [animationsEnabled])

  /**
   * Suspend CSS animations while the tab is backgrounded
   *
   * @effect pauseAnimationsWhenHidden
   * @businessLogic Toggles the global `animations-paused` body class directly
   *   from the Page Visibility event. Deliberately not React state: this
   *   provider wraps the app root, so a tab switch must not re-render every
   *   motion consumer just to flip a body class. Decorative keyframes stop
   *   consuming the GPU compositor in a hidden tab and resume on return.
   * @sideEffects Modifies document.body.classList
   */
  useEffect(() => {
    const syncPaused = () => {
      document.body.classList.toggle(
        'animations-paused',
        document.visibilityState === 'hidden'
      )
    }
    syncPaused()
    document.addEventListener('visibilitychange', syncPaused)
    return () => document.removeEventListener('visibilitychange', syncPaused)
  }, [])

  return (
    <MotionContext.Provider
      value={{ animationsEnabled }}
      data-testid="theme-provider"
    >
      {children}
    </MotionContext.Provider>
  )
})

MotionProvider.displayName = 'MotionProvider'
